// 신호 존 — **이름 없는 카운트다운은 존재하지 않는다**
//
// ── 이 파일의 계약은 조건문 하나다 ──────────────────────────────────────────
//
// 혼동 방지 규칙 넷 중 첫째가 「지금 보고 있는 신호가 누구 것인지 카운트다운 바로 위에 항상
// 크게 표시한다」이고, 이것은 통과·불통 항목이라 부분 점수가 없다. 지키는 방법은 검사가
// 아니라 구조다 — 이름과 숫자를 **같은 조건 안에서 함께 태어나고 함께 사라지게** 두면,
// 이름 없는 카운트다운은 고쳐지는 것이 아니라 **만들어질 수 없다.**
//
// 각각 따로 조건을 걸면 언젠가 한쪽 조건만 바뀌어 둘이 갈린다. 그때 화면에는 숫자가 흐르고
// 있는데 그것이 누구 것인지는 어디에도 없으며, **그 상태가 정상으로 보인다.**
//
// props 의 모양도 같은 뜻이다. 이름과 시각을 **한 값으로 묶어** 받으므로, 이름이 빈 채로
// 시각만 넘기는 호출은 타입 검사를 통과하지 못한다.
//
// ── 무선택일 때 여기 무엇이 사는가 ──────────────────────────────────────────
//
// 빈 화면을 두지 않는다. 빈 자리는 미구현인지 고장인지 구별되지 않는다. 대신 **무엇을 하면
// 되는지 한 줄**과, **왜 화면이 대신 골라 주지 않는지**를 적는다. 뒤의 것을 적는 이유는 그
// 침묵이 불친절이 아니라 설계이기 때문이다 — 계정을 여럿 쓰는 사람에게는 접속 ID 와 이번
// 작전에서 맡은 자리가 다를 수 있고, 그때 화면이 고른 것은 **틀린 주장**이 된다.

import { formatRemaining, formatSinceOpen } from './live-clock.ts'
import { openPassed } from './phase.ts'
import { view as viewStrings, type Lang } from '../../../i18n/index.ts'
import type { SignalStatus } from '../../audio/runtime.ts'
import { useCallback, useRef } from 'react'

import { blockedReasonText } from '../../audio/reason-text.ts'
import { Icon, IconTile } from '../../ui/icons.tsx'
import type { OpenOutcome } from '../../signal/open-outcome.ts'

export interface SignalZoneProps {
  lang: Lang
  /** 고른 줄. **없으면 `null` 이고, 그때 카운트다운도 함께 없다.** */
  target: { rosterName: string; openMs: number } | null
  /** 자기 동기 시계의 지금. **서버가 보내는 틱이 아니다.** */
  nowMs: number
  /** 소리 쪽이 지금 어떤 상태인가와, 잠김을 푸는 문과 끄고 켜는 문. */
  signal: SignalSurface
  /**
   * 고른 rally 의 개시 순간에 일어난 일(`open-outcome.ts`). 모르면 `null`. **개시가 지난 뒤의 상자는
   * 지금 손잡이가 아니라 이것이 고른다** (2026-10-04 셋째 판 뒤, 사용자).
   */
  outcome: OpenOutcome | null
  /** 결과를 정했는가(모름으로 정한 것 포함). 정하기 전의 `null` 과 모름으로 정한 `null` 을 가른다. */
  outcomeSettled: boolean
  /** 결과를 모르는 rally 에서도 이어 고른 채 런타임이 제때 울렸다고 한 적이 있는가(`open-outcome.ts`). */
  rangSeen: boolean
}

/**
 * 화면이 소리에 대해 알아야 하는 것 전부. **훅이 내는 것보다 좁게 받는다.** 관측값
 * (누락 수와 재예약 수)은 이 존이 그리지 않으므로 여기 없다.
 */
export interface SignalSurface extends SignalStatus {
  arm: () => void
  muted: boolean
  toggleMuted: () => void
  hasAudio: boolean
}

/**
 * 신호 존 머리의 높이를 문서 전체의 CSS 변수 `--signal-head-h` 로 알린다. 카드 이름 줄이 상단 고정줄과 이 머리 바로 아래에
 * 붙으려면 이 높이가 필요하다. 머리가 사라지면(아무것도 안 고름, 화면을 떠남) 0 으로 돌린다.
 *
 * **effect 가 아니라 콜백 ref 로 잰다** (2026-10-07). 머리는 고른 것이 있을 때만 그려져 effect 로 재려면 렌더마다 다시
 * 돌려야 했는데, 열람 화면은 0.1초마다 그려지므로 그때마다 변수를 0 으로 되돌렸다 다시 써 문서 전체를 다시 계산했다(적대
 * 리뷰가 짚었다). 콜백 ref 는 머리가 붙고 떨어질 때만 불리고, 붙어 있는 동안의 높이 변화는 ResizeObserver 가 받는다.
 */
function useSignalHeadHeight(): (node: HTMLDivElement | null) => void {
  const observer = useRef<ResizeObserver | null>(null)
  return useCallback((node: HTMLDivElement | null) => {
    const root = document.documentElement
    observer.current?.disconnect()
    observer.current = null
    if (node === null) {
      root.style.setProperty('--signal-head-h', '0px')
      return
    }
    const publish = (): void => {
      root.style.setProperty('--signal-head-h', `${String(node.offsetHeight)}px`)
    }
    publish()
    observer.current = new ResizeObserver(publish)
    observer.current.observe(node)
  }, [])
}

export function SignalZone(props: SignalZoneProps): React.ReactElement {
  const t = viewStrings(props.lang)
  const headRef = useSignalHeadHeight()

  if (props.target === null) {
    return (
      <section
        data-testid="signal-empty"
        aria-label={t.pickTitle}
        className="card mt-block"
      >
        <div className="panel-head cat-accent">
          <IconTile name="listen" tone="accent" />
          <h2 className="text-base font-semibold">{t.pickTitle}</h2>
        </div>
        <p className="mt-line text-sm text-ink-dim">{t.pickBody}</p>
        {/* 침묵의 이유를 적는다. 안 적으면 「고장났나」로 읽히고, 그렇게 읽은 사람이
            다음 회차에 「자동으로 골라 주자」를 요청한다. */}
        <p className="mt-group text-xs text-ink-mute">{t.pickWhy}</p>
      </section>
    )
  }

  const remainingMs = props.target.openMs - props.nowMs
  // **지나간 순간을 「앞으로 올 것」이라고 말하지 않는다.** 지나갔다는 사실을 감추지 않는
  // 원칙은 그대로이고, 그것을 음수를 흘리는 방식으로 지키지 않을 뿐이다.
  // 판단은 카드의 단계와 같은 하나다(`phase.ts` 의 `openPassed`). 개시 그 순간은 아직 지나지 않았다.
  const passed = openPassed(props.target.openMs, props.nowMs)

  return (
    <>
      {/* **이름과 숫자는 한 덩어리로 상단 고정줄 바로 아래에 붙는다** (T6 개정 셋째, 2026-10-05 사용자). 한 상자에
          함께 두기만 했을 때는 목록을 조금 내리면 이름만 고정줄 밑에 숨고 큰 숫자는 남아, 화면에 이름 없는 카운트다운이
          섰다(대리 관측이 360 폭 150px, 1280 폭 169px 에서 잡았다. 혼동 방지 규칙의 첫째이고 통과와 불통만 있는 항목이다).
          그래서 이 머리는 `main` 바로 밑의 형제로 두고 sticky 로 붙인다. 부모가 짧은 상자면 sticky 가 그 상자 안에서만
          붙어 함께 밀려난다. 배경은 아래 몸과 같은 색(네이비 위 주황 5%)을 불투명하게 칠해 카드가 비쳐 보이지 않게 한다.
          소리 상자와 손잡이는 몸에 남아 스크롤된다. */}
      <div
        ref={headRef}
        data-testid="signal-head"
        className="signal-card signal-head sticky top-[var(--topbar-h,0px)] z-[5] mt-block rounded-t-card border-b-0 px-4 pt-group pb-line sm:px-5 short:flex short:flex-wrap short:items-baseline short:gap-x-3 short:pt-line"
      >
      {/* 장식(aria-hidden): 지금 듣는 것이라는 표지. 이름 줄 오른쪽 위에 서고 큰 숫자와는 줄이 다르다. 한 줄로 접히는 낮은 화면에서는 숨는다. */}
      <IconTile name="listen" tone="accent" size="sm" className="absolute right-4 top-3 short:hidden sm:right-5" />
      {/* **이름이 위, 숫자가 아래.** 정본이 「카운트다운 바로 위에」라고 적은 그 자리다.
          순서를 뒤집으면 눈이 숫자를 먼저 잡고 그 다음에야 누구 것인지 확인하게 되는데,
          급한 순간에는 그 확인이 생략된다. **높이 480px 이하 화면만 예외로 한 줄**(이름, 라벨, 숫자 순)로 접는다.
          세 줄로 쌓으면 그 화면에서는 머리와 고정줄이 화면을 다 덮는다(2026-10-05 사용자). 이름은 자르지 않고 줄을
          넘겨, 숫자보다 먼저 읽히는 자리(왼쪽)를 지킨다. 숫자는 그래도 이 머리에서 가장 큰 글자다. 한 줄 머리에서 이름은 flex
          항목이라 `min-w-0` 이 없으면 띄어쓰기 없는 긴 이름이 줄을 넘기지 못하고 화면 밖으로 넘쳤다(2026-10-07 적대 리뷰). */}
      <p data-testid="signal-roster" className="pr-10 text-2xl font-semibold short:min-w-0 short:pr-0 short:text-lg">
        {props.target.rosterName}
      </p>
      <p className="mt-line text-xs font-medium text-ink-dim short:mt-0">{passed ? t.openPassed : t.columnOpen}</p>
      <p
        data-testid="signal-countdown"
        // 개시를 지나도 **숫자를 치우지 않는다** — 지나갔다는 사실이 화면에 남아야 사람이
        // 자기가 놓친 것을 안다. 0 에서 멈춰 세우면 아직 누를 수 있는 것처럼 보인다.
        // 다만 라벨과 부호는 함께 뒤집는다: 지나간 순간을 「개시까지」로 세면 그 큰 글자가
        // 거짓말이 되고, 이 화면에서 가장 큰 글자가 하는 거짓말이 가장 비싸다.
        //
        // 개시가 지나면 숫자의 톤도 한 칸 낮춘다(2026-10-14 사용자 지시 「개시 관련 강조를 낮춘다」, 모양은 세션 선택).
        // 크기는 그대로 두어 머리 높이가 뛰지 않고, 잉크만 ink-dim 과 중간 굵기로 내린다. 그러면 바로 아래 소리 상자의
        // 「지나갔습니다」가 먼저 읽힌다. 이름 줄과 머리의 물들임(고른 rally 라는 표시)은 바꾸지 않는다.
        className={`font-mono text-4xl tracking-tight tabular-nums short:ml-auto short:text-xl ${
          passed ? 'font-medium text-ink-dim' : 'font-semibold'
        }`}
        data-passed={String(passed)}
      >
        {passed ? formatSinceOpen(-remainingMs) : formatRemaining(remainingMs)}
      </p>
      </div>
      {/* `flow-root` 가 머리와 몸을 붙여 둔다. 위 테두리가 없는 상자라 첫 문단의 위 여백이 상자 밖으로 새어(여백 겹침) 머리와
          몸 사이에 12px 의 빈 띠가 생기고 테두리가 끊겼다(2026-10-05 디자인 미리보기 캡처에서 세션이 찾았다). */}
      <section
        data-testid="signal-zone"
        aria-label={t.title}
        className="signal-card flow-root rounded-b-card border-t-0 px-4 pb-group sm:px-5"
      >
        <SoundState
          lang={props.lang}
          signal={props.signal}
          passed={passed}
          outcome={props.outcome}
          outcomeSettled={props.outcomeSettled}
          rangSeen={props.rangSeen}
        />
      </section>
    </>
  )
}

/**
 * 소리 쪽이 지금 무슨 상태인지 말한다.
 *
 * **소리가 안 나는 자리마다 왜 안 나는지가 적혀 있어야 한다.** 침묵은 고장과 구별되지
 * 않고, 구별되지 않으면 사람은 그것을 자기 탓으로 돌리거나 앱을 믿지 않게 된다. 도메인
 * 없는 HTTP 에서 소리가 안 나는 경로가 여럿이라 「안 났다」만으로는 무엇을 해야 할지도
 * 갈리지 않는다 — 무음 스위치인지, 한 번 눌러야 하는 것인지, 이미 지나간 것인지에 따라
 * 사람이 할 일이 전혀 다르다.
 */
function SoundState(props: {
  lang: Lang
  signal: SignalSurface
  passed: boolean
  outcome: OpenOutcome | null
  outcomeSettled: boolean
  rangSeen: boolean
}): React.ReactElement {
  return (
    <>
      <SoundBox
        lang={props.lang}
        signal={props.signal}
        passed={props.passed}
        outcome={props.outcome}
        outcomeSettled={props.outcomeSettled}
        rangSeen={props.rangSeen}
      />
      <MuteToggle lang={props.lang} signal={props.signal} />
    </>
  )
}

/**
 * 소리를 끄고 켜는 손잡이. **상태가 무엇이든 같은 자리에 뜬다.**
 *
 * 위의 상자는 조기 반환 사슬이라 상태마다 다른 것이 그려지는데, 이 손잡이를 그 안 한
 * 갈래에 두면 나머지 갈래에서 사라진다. 그러면 **놓친 뒤에는 끌 수 없고 잠긴 동안에도
 * 끌 수 없다.** 「켜고 끄는 것은 사용자 의지」(2026-08-25 결정)가 앱의 사정에 따라
 * 조건부가 되는 것이라, 자리를 사슬 밖 형제로 둔다.
 *
 * ── 왜 잠김 상자의 버튼과 글자가 안 겹치는가 ────────────────────────────────
 *
 * 잠긴 채로 켜져 있으면 위 상자의 버튼은 「소리 켜기」이고 이 손잡이는 「소리 끄기」다.
 * 껐으면 위는 껐다는 상자라 잠김 상자 자체가 안 뜨고, 이 손잡이만 「소리 켜기」가 된다.
 * **같은 글자의 버튼 둘이 한 화면에 서는 조합이 없다.**
 *
 * ── 왜 44픽셀이 아슬아슬한가 ────────────────────────────────────────────────
 *
 * `py-control` 은 14픽셀이고 11픽셀 글자의 행 상자가 약 16픽셀이라 14+14+16 = 44 인데,
 * 브라우저의 반올림이 43.94 로 떨어뜨린다. 테두리 2픽셀이 그 차이를 메운다. 그래서
 * `border` 는 장식이 아니라 **하한을 지키는 부품**이고, 지우면 좁은 폭 검사 둘이 빨개진다.
 */
function MuteToggle(props: { lang: Lang; signal: SignalSurface }): React.ReactElement | null {
  const t = viewStrings(props.lang)
  const { signal } = props

  // 소리를 낼 장치가 없는 브라우저에서는 끌 것도 없다. 눌러도 아무 일이 없는 버튼은
  // 고장으로 읽히고, 하필 소리가 안 나는 순간이라 사람이 그것을 원인으로 오해한다.
  if (!signal.hasAudio) return null

  return (
    <div className="mt-group">
      <button
        type="button"
        data-testid="signal-mute-toggle"
        // **`aria-pressed` 를 일부러 안 단다.** 같은 화면의 정렬 토글과 듣기 버튼은 그것을
        // 쓰지만, 저들은 눌렸을 때 이름이 상태로 바뀌어(「듣는 중」) 눌림과 이름이 같은
        // 방향을 가리킨다. 이 손잡이는 이름이 **행동**이라 방향이 뒤집힌다: 껐을 때 이름은
        // 「소리 켜기」인데 눌림은 참이라, 낭독기가 「소리 켜기, 눌림」 즉 **소리가 나고
        // 있다**로 읽는다. 눈으로 보는 사람은 테두리와 배경으로 아는 사실을 소리로 읽는
        // 사람만 정반대로 듣는 것이다. (2026-09-04 독립 반증이 잡았다.)
        //
        // 이름을 `aria-label` 로 고정해 방향을 맞추는 길은 안 골랐다. 눈에 보이는 글자와
        // 접근 가능한 이름이 갈라져 음성 조작(「소리 켜기」라고 말해 누르기)이 깨진다.
        // 떼고 나면 「소리 켜기, 버튼」으로 읽혀 **누르면 무엇이 되는지**가 그대로 전달되고,
        // 지금 어느 쪽인지는 개시 전에는 바로 위 형제인 「소리를 껐습니다」 상자가 말하고, 개시 뒤에는 상자를 개시 순간의 결과가 고르므로 이 버튼의 글자가 말한다.
        className="btn btn-secondary"
        onClick={signal.toggleMuted}
      >
        <Icon name={signal.muted ? 'speaker' : 'speaker-off'} />
        {signal.muted ? t.unmuteLabel : t.muteLabel}
      </button>
    </div>
  )
}

/**
 * `passed` 는 위의 큰 숫자가 쓰는 것과 같은 판단이다(개시 그 순간은 아직 지나지 않았다). 끈 상태
 * 안내의 둘째 문장이 그것을 따라 갈리므로, 여기서 따로 시각을 견주면 숫자와 문장이 다른 순간에
 * 뒤집히는 한 틱이 생긴다.
 */
function SoundBox(props: {
  lang: Lang
  signal: SignalSurface
  passed: boolean
  outcome: OpenOutcome | null
  outcomeSettled: boolean
  rangSeen: boolean
}): React.ReactElement {
  const t = viewStrings(props.lang)
  const { signal } = props

  // **개시가 지난 뒤에는 개시 순간의 결과가 상자를 고른다** (2026-10-04 셋째 판 뒤, 사용자). 지금 손잡이로
  // 고르면 개시 뒤에 끄거나 켜는 순간 이유가 다시 쓰였다. 제때 울린 사람이 다시 켜면 늦은 사람에게 하는
  // 빨간 경고를 봤고, 제때 울린 뒤 끄면 끈 채 지난 것처럼 읽혔다(셋째 판 대리 관측 둘). 끈 상태는 아래
  // 손잡이 글자(「소리 켜기」)가 늘 말한다.
  //
  // 개시 뒤의 표(2026-10-04 넷째 판 뒤, 사용자): 제때 울림은 「제때 울렸습니다」, 꺼져 있었음은 끈 채면 끈 상태
  // 안내이고 켜면 회색 상자, 고른 채 못 울림은 빨간 「제때 울리지 못했습니다」, 못 봄(개시 뒤 고름 등)은 늦은
  // 소리를 내지 않는다는 경고다. 못 울림과 못 봄을 같은 문장으로 접으면 제때 고른 사람이 자기가 늦었다고
  // 읽었고(넷째 판 대리 관측 둘), 그 뒤에 끄면 끈 것이 원인처럼 읽혔다.
  // **끈 동안에는 둘째 문장에 켜기를 넣는다** (T6 개정 셋째, 2026-10-05 사용자). 끈 동안에는 다른 rally 를 골라도
  // 켜기 전에는 울리지 않으므로, 켜기 없는 「고르면 울린다」는 사실이 아니다. 결과를 모르는데 런타임이 울렸다고 했던
  // rally 는 끈 뒤에도 같은 상자다(`rangSeen`, 다섯째 판 리뷰).
  if (
    props.passed &&
    (props.outcome === 'rang' || (props.outcome === null && (props.rangSeen || signal.state === 'passed')))
  ) {
    return <PassedBox lang={props.lang} muted={signal.muted} />
  }
  if (props.passed && props.outcome === 'muted' && !signal.muted) {
    return (
      <div
        data-testid="signal-off-at-open"
        className="note mt-group"
      >
        <p className="text-sm font-semibold">{t.missedTitle}</p>
        <p className="mt-line text-xs text-ink-dim">{t.offAtOpenBody}</p>
      </div>
    )
  }
  if (props.passed && props.outcome === 'missed') {
    return (
      <div
        data-testid="signal-not-rung"
        className="note note-strike mt-group"
      >
        <p className="text-lg font-semibold text-[#FF5C8A]">{t.missedTitle}</p>
        <p className="mt-line text-sm text-ink-dim">
          {signal.muted ? t.appMissedBodyMuted : t.appMissedBody}
        </p>
      </div>
    )
  }
  // 늦음 경고는 「모름」으로 **정해졌을 때만**이다. 끈 채 개시를 넘기는 첫 렌더에서는 결과가 아직 없어, 정하기
  // 전의 `null` 로 이 상자를 그리면 스스로 끈 사람에게 빨간 경고가 먼저 칠해졌다(다섯째 판 리뷰). 정하기 전에는
  // 아래 끈 상태 안내가 선다.
  if (props.passed && props.outcome === null && props.outcomeSettled && signal.muted) {
    return <LateBox lang={props.lang} />
  }

  // **(위의 개시 뒤 표를 지나면) 사람이 끈 것이 맨 앞이다.** 아래 갈래들은 앱이나 브라우저나 시간이 만든 상태이고
  // 이것은 사람이 만든 상태라, 뒤에 두면 「잠겼습니다, 눌러서 푸세요」가 자기가 끈 사람에게
  // 뜬다. 그것은 원인을 잘못 가리키는 안내이고, 그 말을 믿은 사람은 버튼을 눌러도 소리가
  // 안 나는 것을 보고 앱이 고장났다고 읽는다.
  if (signal.muted) {
    return (
      <div
        data-testid="signal-muted"
        className="note note-sunk mt-group"
      >
        <p className="text-sm font-semibold">{t.mutedTitle}</p>
        {/* **개시 전후로 둘째 문장이 갈린다** (2026-09-17 결정, 2026-10-02 문장 정정). 개시 전에는
            「다시 켜면 그때부터 울립니다」가 참이지만, 지난 뒤에 그 말을 믿고 켠 사람은 곧바로
            「소리를 내지 않습니다」를 본다(2026-09-04 대리 관측의 감점). 지난 뒤의 문장에 켜기가
            들어간 것은 끔이 이 rally 하나가 아니라 화면 전체에 걸리기 때문이다. 다른 rally 를
            골라도 켜기 전에는 울리지 않는다. */}
        <p className="mt-line text-xs text-ink-dim">
          {props.passed ? t.mutedBodyPassed : t.mutedBody}
        </p>
      </div>
    )
  }

  // **개시가 지나갔다는 것은 크게 말한다.** 소리를 내지 않기로 한 것은 결정이지 고장이
  // 아니고, 그 결정이 화면에 안 보이면 사람은 조용한 화면 앞에서 자기 차례가 지나간 줄도
  // 모른다. 늦은 소리를 내는 편이 친절해 보이지만 그 소리를 개시로 믿은 사람은 **더 늦게**
  // 누른다 — 침묵보다 나쁘다.
  if (signal.state === 'missed') {
    return <LateBox lang={props.lang} />
  }

  // **울렸고, 그 순간이 지났다.** 못 울린 것과 같은 상자로 말하면 정상 동작이 매번 경보로
  // 읽히고, 경보가 흔해지면 진짜 경보도 안 읽힌다. 그래서 같은 제목을 쓰되 톤을 낮춘다 —
  // 얼마나 지났는지는 바로 위의 큰 숫자가 이미 흐르게 적고 있다.
  //
  // 이 상자가 **개시를 지나는 순간** 뜨는 것이 이 회차가 고친 자리다. 예전에는 어긋남
  // 검사가 우연히 발화할 때까지 6~17초 동안 「소리는 고른 하나에서만 납니다」가 그대로
  // 떠 있었다. (2026-08-14 대리 관측.)
  if (signal.state === 'passed') {
    return <PassedBox lang={props.lang} muted={false} />
  }

  // 브라우저는 사람이 화면을 한 번 누르기 전에는 소리를 내지 않는다. 그 사실을 말하지
  // 않으면 「이 앱은 소리가 안 나네」로 읽히고, 그렇게 읽은 사람은 다시 켜지 않는다.
  if (signal.state === 'locked') {
    return (
      <div
        data-testid="signal-locked"
        className="note note-main mt-group"
      >
        <p className="text-sm font-semibold">{t.lockedTitle}</p>
        <p className="mt-line text-xs text-ink-dim">{blockedReasonText(signal.reason, props.lang)}</p>
        <button
          type="button"
          data-testid="signal-arm"
          onClick={signal.arm}
          className="btn btn-primary mt-group"
        >
          <Icon name="speaker" />
          {t.armLabel}
        </button>
      </div>
    )
  }

  return (
    <>
      <p className="mt-group text-xs text-ink-mute">{t.soundOne}</p>
      {/* 조용히 적게 울리면 사람은 박자를 놓친 것을 자기 탓으로 안다. 몇 개를 놓쳤는지
          말해 주면 남은 박과 출발음은 제자리라는 것도 함께 말하게 된다. */}
      {signal.droppedMarkers > 0 ? (
        <p data-testid="signal-dropped" className="mt-line text-xs text-ink-mute">
          {t.droppedMarkers.replaceAll('{n}', String(signal.droppedMarkers))}
        </p>
      ) : null}
      {signal.reason === '' ? null : (
        <p data-testid="signal-note" className="mt-line text-xs text-ink-mute">
          {blockedReasonText(signal.reason, props.lang)}
        </p>
      )}
    </>
  )
}

/** 제때 울렸고 그 순간이 지났다. 못 울린 것과 같은 제목을 쓰되 톤을 낮춘다(위 `SoundBox` 주석). */
function PassedBox(props: { lang: Lang; muted: boolean }): React.ReactElement {
  const t = viewStrings(props.lang)
  return (
    <div data-testid="signal-passed" className="note mt-group">
      <p className="text-sm font-semibold">{t.missedTitle}</p>
      <p className="mt-line text-xs text-ink-dim">{props.muted ? t.passedBodyMuted : t.passedBody}</p>
    </div>
  )
}

/** 늦어서 소리를 걸지 않았다. 늦은 소리를 개시로 믿으면 더 늦게 누르게 된다(위 `SoundBox` 주석). */
function LateBox(props: { lang: Lang }): React.ReactElement {
  const t = viewStrings(props.lang)
  return (
    <div
      data-testid="signal-missed"
      className="note note-strike mt-group"
    >
      <p className="text-lg font-semibold text-[#FF5C8A]">{t.missedTitle}</p>
      <p className="mt-line text-sm text-ink-dim">{t.missedBody}</p>
    </div>
  )
}
